import { debounce } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useReducer,
} from 'react';
import { RsSpeechAPI } from 'interfaces';
import { DOCUMENT_STATES, TEXT_TO_SPEECH } from 'constants';
import {
  BookLocationDocumentValuesContext,
  BookLocationModifiersContext,
  BookLocationValuesContext,
  BookValuesContext,
  DocumentValuesContext,
  TextToSpeechModifiersContext,
  TextToSpeechValuesContext,
  UserAgentValuesContext,
} from 'contexts';
import {
  appendSvg,
  filterVoiceList,
  getCharacterOffsetFromSelection,
  getInitialVoice,
  getVoiceMap,
  reflectCurrentTTSWordObjects,
  removeTTSClass,
  removeSvg,
} from 'utilities/ttsUtilities';
import { BookDocumentInformation } from 'types';
import { AriaLiveRegion } from 'components';
import { getLocalSettingsProxy } from 'utilities';
import * as TTSActions from './actions';
import { ttsInitialState, ttsReducer } from './reducer';

export const TextToSpeechProvider = ({ children }) => {
  /**
   * Getting context values from providers
   */
  const { getDocumentInformation } = useContext(BookValuesContext);
  const { goToLocation } = useContext(BookLocationModifiersContext);
  const currentDocumentIndex = useContext(BookLocationValuesContext)
    .currentBookLocation.documentIndex;
  const nextDocumentLocationWithText = useContext(
    BookLocationDocumentValuesContext,
  ).nextDocumentLocationWithText;
  const {
    frame,
    frameDocument,
    state: documentState,
  } = useContext(DocumentValuesContext);
  const { userAgent } = useContext(UserAgentValuesContext);
  const frameDocumentRef = useRef();

  /**
   * Getting local settings
   * TTS settings are not per-ePub, so we do not need to pass an epub PK into getLocalSettingsProxy
   */
  const [localSettings] = useState(getLocalSettingsProxy);

  /***
   * State values and refs
   */
  const [state, dispatch] = useReducer(ttsReducer, ttsInitialState);

  useEffect(
    function checkIfAnyTextForTTSToRead() {
      dispatch(
        TTSActions.setHasAnyText(
          !!frameDocument?.body?.textContent.trim().length,
        ),
      );
    },
    [frameDocument],
  );

  // Track whether the user's device fires word boundary events.
  const areWordBoundaryEventsSupported = useRef();

  // Whether TTS is currently speaking
  const isSpeakingRef = useRef();
  useEffect(
    function syncIsSpeakingRef() {
      isSpeakingRef.current = state.isSpeaking;
    },
    [state.isSpeaking],
  );

  // Whether TTS is paused
  const isPausedRef = useRef();
  useEffect(
    function syncIsPausedRef() {
      isPausedRef.current = state.isPaused;
    },
    [state.isPaused],
  );

  // Whether the next document to load should start reading immediately.
  const shouldReadFromDocumentIndex = useRef(null);

  // The character offset at which the service is currently reading. Retained when paused.
  const currentCharacterOffsetRef = useRef(0);

  // The offset of the current word being read.
  const [currentWordOffset, setCurrentWordOffset] = useState(0);
  const currentWordOffsetRef = useRef(currentWordOffset);
  useEffect(
    function updateCurrentWordOffsetRef() {
      currentWordOffsetRef.current = currentWordOffset;
    },
    [currentWordOffset],
  );

  // A list of voices from the speech API
  const [voices, setVoices] = useState();

  // API abstraction for speech - create a RsSpeechAPI object for this Provider to use.
  // (setter for rsSpeechAPI needs to exist for useState to work)
  const [rsSpeechAPI, _] = useState(() => {
    const ttsSettings = localSettings.textToSpeech;
    return new RsSpeechAPI({
      getVolume: () => ttsSettings.volume,
      getVoice: () => ttsSettings.voice,
      getRate: () => ttsSettings.rate,
      // When the page first loads, the voices are not available.
      onVoicesChanged: () => {},
    });
  });

  // A flag to set that makes reading adapt gracefully if the content is altered.
  const restartOnNextBoundaryRef = useRef(false);

  // Whether positioning data is available and up to date
  const [isPositioningValid, setIsPositioningValid] = useState(false);

  // A resize observer to watch for changes to the document sizing.
  const documentResizeObserverRef = useRef(null);

  // Track positioning computation status.
  const computePositioningInProgress = useRef(false);
  const computePositioningNeedsRerun = useRef(false);
  const computePositioningWidth = useRef(0);
  const computePositioningHeight = useRef(0);

  // bookDocumentInformation for current document.
  const [bookDocumentInformation, setBookDocumentInformation] = useState();
  const bookDocumentInformationRef = useRef(bookDocumentInformation);
  useEffect(
    function updateBookDocumentInformationRef() {
      bookDocumentInformationRef.current = bookDocumentInformation;
    },
    [bookDocumentInformation],
  );

  // Checks whether the frame body is valid and ready for TTS.
  const [isDocumentReady, setIsDocumentReady] = useState(false);
  const isDocumentReadyRef = useRef(isDocumentReady);

  useEffect(
    function updateIsDocumentReadyRef() {
      if (frameDocument !== frameDocumentRef.current) {
        frameDocumentRef.current = frameDocument;
        setIsDocumentReady(false);
      }

      isDocumentReadyRef.current =
        documentState === DOCUMENT_STATES.FINISHED && // document loading is completed
        Boolean(frameDocumentRef.current?.body); // and iframe has body

      setIsDocumentReady(isDocumentReadyRef.current);
    },
    [documentState, frameDocument],
  );

  // Check if TTS is ready to start
  const isReadyRef = useRef(false);
  // this is necessary in order for the underline line to be displayed in the right place
  const [isReady, setIsReady] = useState(false);
  const checkAndSetRefAndStateIsReady = useCallback(() => {
    isReadyRef.current =
      isDocumentReadyRef.current && // document status is valid
      Boolean(bookDocumentInformationRef.current) && // bookDocumentInformation is available
      bookDocumentInformationRef.current?.bookDocument?.index ===
        currentDocumentIndex; // and for the correct document

    setIsReady(isReadyRef.current);
  }, [currentDocumentIndex]);

  useEffect(
    function updateIsReady() {
      checkAndSetRefAndStateIsReady();
    },
    [
      bookDocumentInformation,
      bookDocumentInformationRef,
      checkAndSetRefAndStateIsReady,
      isDocumentReady,
      isDocumentReadyRef,
    ],
  );

  // get bookDocumentInformation for the current document
  const updateBookDocumentInformation = useCallback(async () => {
    if (!isDocumentReady) {
      console.warn('Cannot update bookDocumentInformation without a document.');
      bookDocumentInformationRef.current = null;
      return;
    }

    // get an instance of BookDocumentInformation as soon as the content is available
    if (
      bookDocumentInformationRef.current?.bookDocument?.index !==
      currentDocumentIndex
    ) {
      isReadyRef.current = false;
      bookDocumentInformationRef.current = await getDocumentInformation(
        currentDocumentIndex,
        frameDocument,
        BookDocumentInformation.MODES.COMPUTE_SPATIAL_INFO,
      );
      setBookDocumentInformation(bookDocumentInformationRef.current);
      checkAndSetRefAndStateIsReady();
    }
  }, [
    checkAndSetRefAndStateIsReady,
    currentDocumentIndex,
    isDocumentReady,
    frameDocument,
    getDocumentInformation,
  ]);

  // Cancels current utterance and resets tts service.
  const stopReading = useCallback(() => {
    removeTTSClass(frameDocument);
    removeSvg(frameDocument);
    rsSpeechAPI.cancel();

    currentCharacterOffsetRef.current = 0;
    setCurrentWordOffset(0);
    dispatch(TTSActions.stopReading());
  }, [frameDocument, rsSpeechAPI]);

  // Pauses reading, cancels active synthesis but preserves state.
  // SpeechSynthesis API provides a pause method, but we are not using it because it does not allow modifications to the active speech event.
  const pauseReading = useCallback(() => {
    rsSpeechAPI.cancel();
    dispatch(TTSActions.pauseReading());
  }, [rsSpeechAPI]);

  // Computing positioning data.
  const computePositioning = useCallback(async () => {
    if (
      bookDocumentInformationRef.current?.bookDocument?.index !==
      currentDocumentIndex
    ) {
      return;
    }

    if (!isReadyRef.current) {
      console.warn('Cannot compute TTS positioning without a body.');
      computePositioningInProgress.current = false;
      computePositioningNeedsRerun.current = false;
      return;
    }

    if (computePositioningInProgress.current) {
      // if we are already computing, but need to compute again,
      // set a flag to do so after the current computation is done
      computePositioningNeedsRerun.current = true;
      return;
    }

    computePositioningInProgress.current = true;

    bookDocumentInformationRef.current.buildWordObjects(
      BookDocumentInformation.MODES.COMPUTE_SPATIAL_INFO,
    );
    const newWordOffset =
      bookDocumentInformationRef.current.wordOffsetFromCharacterOffset(
        currentCharacterOffsetRef.current,
      );
    setCurrentWordOffset(newWordOffset);
    setIsPositioningValid(true);

    // appending the SVG needs to occur after the #wordObjects structure has been rebuilt
    if (isPausedRef.current) {
      appendSvg(
        frameDocument,
        bookDocumentInformationRef.current.wordObjects[newWordOffset],
      );
    } else if (isSpeakingRef.current) {
      // pause and play to reorient to the correct position
      restartOnNextBoundaryRef.current = true;
    }

    computePositioningInProgress.current = false;

    if (computePositioningNeedsRerun.current) {
      // If we need to re-run the computation, do it now,
      // but await the call so that it cascades into the current call's returned promise.
      computePositioningNeedsRerun.current = false;
      await computePositioning();
    }
  }, [currentDocumentIndex, frameDocument]);

  const debouncedComputePositioning = useMemo(
    () => debounce(computePositioning, 500),
    [computePositioning],
  );

  const computePositioningCaller = useCallback(async () => {
    // if something changes to warrant a recompute, the SVG placement often becomes incorrect
    removeSvg(frameDocument);

    if (isSpeakingRef.current) {
      debouncedComputePositioning(); // Recompute immediately if speaking
    } else {
      setIsPositioningValid(false); // Recompute on the next play if not speaking
    }
  }, [debouncedComputePositioning, frameDocument]);

  useEffect(
    function updateResizeObserver() {
      checkAndSetRefAndStateIsReady();
      if (!isReadyRef.current) {
        // The document index changed, stop reading and remove resizeObserver.
        stopReading();
        documentResizeObserverRef.current?.disconnect();
        computePositioningWidth.current = 0;
        computePositioningHeight.current = 0;
        return;
      }

      documentResizeObserverRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.borderBoxSize?.[0].inlineSize;
          const height = entry.borderBoxSize?.[0].blockSize;
          if (typeof width !== 'number' || typeof height !== 'number') {
            continue;
          }

          if (
            width !== computePositioningWidth.current ||
            height !== computePositioningHeight.current
          ) {
            if (
              computePositioningWidth.current &&
              computePositioningHeight.current
            ) {
              // Recompute positioning on resize if the width or height changes.
              // Dont need to recompute on initial load because the initial positioning is correct
              computePositioningCaller();
            }
            computePositioningWidth.current = width;
            computePositioningHeight.current = height;
          }
        }
      });

      documentResizeObserverRef.current?.disconnect();
      documentResizeObserverRef.current?.observe(frameDocument?.body);

      return () => {
        // Cleanup the resize observer when the component unmounts
        documentResizeObserverRef.current?.disconnect();
        documentResizeObserverRef.current = null;
      };
    },
    [
      computePositioningCaller,
      checkAndSetRefAndStateIsReady,
      isReady,
      frameDocument?.body,
      stopReading,
    ],
  );

  // Given a word offset as a starting position, builds a series of utterances from the content, and speaks them.
  // New utterances are created when line breaks occur, in order to add natural pauses.
  const speak = useCallback(
    async (wordObjectOffset) => {
      // This TTS feature itself sometimes disrupts the DOM references in the book document information object,
      // which can result in a bug where the "read from here" feature starts over from the beginning of the document.
      // We need to signal that the positioning is no longer valid so that it can be recomputed on the next read.
      setIsPositioningValid(false);

      let text = '';
      let encounteredNewline = false;
      let firstWordObjectOffset = wordObjectOffset;
      let wordObjectOffsetBreakpoint = firstWordObjectOffset + 1000;

      const wordObjects = [];
      while (!encounteredNewline) {
        let wordObject =
          bookDocumentInformationRef.current.wordObjects[wordObjectOffset];
        if (wordObject && wordObjectOffset <= wordObjectOffsetBreakpoint) {
          text += wordObject.wordText;
          text += ' ';
          wordObjectOffset += 1;
          encounteredNewline = wordObject.endsInNewline;
        } else {
          break;
        }

        wordObjects.push(wordObject);
      }

      if (!text.length) {
        stopReading();

        if (
          nextDocumentLocationWithText &&
          localSettings.textToSpeech.shouldContinueToNextDocument
        ) {
          // Advance to the next document when finished reading.

          const liveRegion = document.getElementById(
            'tts-announcement-aria-live-region',
          );
          liveRegion.textContent = 'Continuing to next document';

          shouldReadFromDocumentIndex.current =
            nextDocumentLocationWithText.documentIndex;
          frameDocumentRef.current = null;
          await goToLocation(nextDocumentLocationWithText);

          // Give the screen reader time to announce the message.
          setTimeout(() => {
            liveRegion.textContent = '';
          }, 10000);
        }

        return;
      }

      // If word boundary events are not supported by the user's device, or if we haven't detected whether it is supported yet,
      // we can fall back on a less accurate system for highlighting the words that TTS is reading.
      // The two methods (less accurate here and more accurate in onBoundary) will conflict with one another, so we can only use one.
      if (areWordBoundaryEventsSupported.current !== true) {
        const characterOffset = wordObjects[0].characterOffset;

        currentCharacterOffsetRef.current = characterOffset;
        setCurrentWordOffset(
          bookDocumentInformationRef.current.wordOffsetFromCharacterOffset(
            characterOffset,
          ),
        );

        removeTTSClass(frameDocument);
        reflectCurrentTTSWordObjects(frame, wordObjects);
      }

      const onEnd = async () => {
        // don't speak the next utterance if the user has stopped playback
        if (!isSpeakingRef.current) {
          return;
        }

        await speak(wordObjectOffset);
      };

      const onError = async (event) => {
        console.warn('TTS speech synthesis error:', event);
        // On error, stop TTS to prevent further issues
        // This prevents the crash detection from being triggered by repeated errors

        // If the error is 'network', it's likely due to connection loss.
        // We should stop reading and potentially notify the user.
        if (event.error === 'network') {
          console.error('TTS: Network error detected during speech synthesis.');
        }

        stopReading();
      };

      const onBoundary = async (event) => {
        areWordBoundaryEventsSupported.current = true;

        // Find the word object that corresponds to the character index of the boundary event.
        let wordsSpoken =
          text.substring(0, event.charIndex).split(' ').length - 1;
        let newCharacterOffset =
          bookDocumentInformationRef.current.wordObjects[
            firstWordObjectOffset + wordsSpoken
          ].characterOffset;

        async function isReadingAtCharacterOffset(characterOffset) {
          currentCharacterOffsetRef.current = characterOffset;

          const newWordOffset =
            bookDocumentInformationRef.current.wordOffsetFromCharacterOffset(
              characterOffset,
            );

          setCurrentWordOffset(newWordOffset);

          const wordObject =
            bookDocumentInformationRef.current.wordObjects[newWordOffset];

          removeTTSClass(frameDocument);
          reflectCurrentTTSWordObjects(frame, [wordObject]);
          removeSvg(frameDocument);
          appendSvg(frameDocument, wordObject);

          if (restartOnNextBoundaryRef.current) {
            restartOnNextBoundaryRef.current = false;
            pauseReading();
            await readFromCharacterOffset(currentCharacterOffsetRef.current);
          }
        }

        return isReadingAtCharacterOffset(newCharacterOffset);
      };

      rsSpeechAPI.speak({
        text,
        onBoundary,
        onEnd,
        onError,
      });
    },
    [
      bookDocumentInformationRef,
      frame,
      frameDocument,
      goToLocation,
      state.hasAnyText,
      nextDocumentLocationWithText,
      pauseReading,
      rsSpeechAPI,
      stopReading,
    ], // `readFromCharacterOffset` intentionally left out of this array
  );

  // Kick off reading from a given character offset.
  // This will run once and call #speak, which will call itself until it is out of content to read.
  const readFromCharacterOffset = useCallback(
    async (characterOffsetToReadFrom) => {
      rsSpeechAPI.cancel();

      if (!isReadyRef.current) {
        return;
      }

      currentCharacterOffsetRef.current = characterOffsetToReadFrom;
      currentWordOffsetRef.current =
        bookDocumentInformationRef.current.wordOffsetFromCharacterOffset(
          characterOffsetToReadFrom,
        );

      dispatch(TTSActions.startReading());

      await speak(currentWordOffsetRef.current);

      shouldReadFromDocumentIndex.current = null;
    },
    [rsSpeechAPI, speak],
  );

  // If TTS is paused, continues reading. Otherwise start reading from the beginning of the content.
  const read = useCallback(async () => {
    if (
      bookDocumentInformationRef.current?.bookDocument?.index !==
      currentDocumentIndex
    ) {
      await updateBookDocumentInformation();
    }

    if (!isPositioningValid) {
      await computePositioning();
    }
    readFromCharacterOffset(currentCharacterOffsetRef.current);
  }, [
    computePositioning,
    currentDocumentIndex,
    isPositioningValid,
    readFromCharacterOffset,
    updateBookDocumentInformation,
  ]);

  /**
   * Callbacks to handle forward and rewind 10 words.
   */
  useEffect(
    function updateHasPreviousAndNext() {
      if (!isReadyRef.current) {
        return;
      }

      dispatch(
        TTSActions.setPreviousAndNext(
          currentWordOffsetRef.current > 0,
          (currentWordOffsetRef.current === 0 && state.hasAnyText) ||
            currentWordOffsetRef.current <
              bookDocumentInformationRef.current.wordObjects.length - 1,
        ),
      );
    },
    [bookDocumentInformation, currentWordOffset, state.hasAnyText],
  );

  // Start reading from a relative position (previous or next) from the current reading position.
  const goToRelativePosition = useCallback(
    async (offset) => {
      if (!isReadyRef.current) {
        return;
      }
      if (!isPositioningValid) {
        await computePositioning();
      }

      const newWordOffset = Math.max(
        0,
        Math.min(
          currentWordOffsetRef.current + offset,
          bookDocumentInformationRef.current.wordObjects.length - 1,
        ),
      );
      const newCharacterOffset =
        bookDocumentInformationRef.current.wordObjects[newWordOffset]
          .characterOffset;

      readFromCharacterOffset(newCharacterOffset);
    },
    [computePositioning, isPositioningValid, readFromCharacterOffset],
  );

  // Start reading from 10 words before the current reading position.
  const goToPrevious = useCallback(
    () => goToRelativePosition(-10),
    [goToRelativePosition],
  );

  // Start reading from 10 words after the current reading position.
  const goToNext = useCallback(
    () => goToRelativePosition(10),
    [goToRelativePosition],
  );

  /**
   * Read Aloud From Here Feature
   */

  // Find CFI for selected text and start reading from its position in the content.
  const readFromCfi = useCallback(
    async (selectedHighlight) => {
      if (
        bookDocumentInformationRef.current?.bookDocument?.index !==
        currentDocumentIndex
      ) {
        await updateBookDocumentInformation();
      }

      if (!isPositioningValid) {
        await computePositioning();
      }

      const characterOffset = getCharacterOffsetFromSelection(
        bookDocumentInformationRef.current,
        frameDocument,
        selectedHighlight,
      );

      return readFromCharacterOffset(characterOffset);
    },
    [
      computePositioning,
      currentDocumentIndex,
      frameDocument,
      isPositioningValid,
      readFromCharacterOffset,
      updateBookDocumentInformation,
    ],
  );

  /**
   * Continuous Reading Feature
   */

  // Start reading from the beginning of the content.
  const readFromBeginning = useCallback(async () => {
    if (
      bookDocumentInformationRef.current?.bookDocument?.index !==
      currentDocumentIndex
    ) {
      await updateBookDocumentInformation();
    }

    if (!isPositioningValid) {
      await computePositioning();
    }

    readFromCharacterOffset(0);
  }, [
    computePositioning,
    currentDocumentIndex,
    isPositioningValid,
    readFromCharacterOffset,
    updateBookDocumentInformation,
  ]);

  // If we want to read from the beginning of the document as soon as it loads,
  // we need to allow all relevant stateful variables to stabilize.
  const debouncedReadFromBeginning = useMemo(
    () => debounce(readFromBeginning, 300),
    [readFromBeginning],
  );

  // If we have a flag set to start reading the next document,
  // then start reading as soon as bookDocumentInformation changes.
  useEffect(
    function readFromDocumentIndex() {
      if (
        !Boolean(frameDocumentRef.current?.body) ||
        frameDocument?.body !== frameDocumentRef.current?.body
      ) {
        return;
      }

      if (
        shouldReadFromDocumentIndex.current &&
        shouldReadFromDocumentIndex.current === currentDocumentIndex &&
        isDocumentReadyRef.current
      ) {
        debouncedReadFromBeginning();
      }
    },
    [
      currentDocumentIndex,
      debouncedReadFromBeginning,
      frameDocument,
      shouldReadFromDocumentIndex,
    ],
  );

  /**
   * TTS Controls: Set the volume, rate, and voice for the TTS service.
   */
  const [volume, _setVolume] = useState(localSettings.textToSpeech.volume);
  const setVolume = useCallback(
    (newValue) => {
      newValue = parseFloat(newValue); // Enforce not a string
      newValue = newValue >= 0 ? newValue : 0; // Enforce not NaN and at least 0
      newValue = Math.min(newValue, 1); // Enforce less than 1

      localSettings.textToSpeech.volume = newValue;
      _setVolume(newValue);

      // Adjust volume while speaking is in progress by restarting the utterance
      if (isSpeakingRef.current) {
        read();
      }
    },
    [localSettings, _setVolume, read],
  );

  const [rate, _setRate] = useState(localSettings.textToSpeech.rate);
  const setRate = useCallback(
    (newValue) => {
      if (!TEXT_TO_SPEECH.OPTIONS.RATE.includes(parseFloat(newValue))) {
        return;
      }

      localSettings.textToSpeech.rate = newValue;
      _setRate(newValue);

      // Adjust rate while speaking is in progress by restarting the utterance
      if (isSpeakingRef.current) {
        read();
      }
    },
    [localSettings, _setRate, read],
  );

  const [voice, _setVoice] = useState(localSettings.textToSpeech.voice);
  const [ttsZoomPreference, _setTTSZoomPreference] = useState(
    localSettings.ttsZoomPreference,
  );

  const setTTSZoomPreference = useCallback(
    (newValue) => {
      localSettings.ttsZoomPreference = newValue;
      _setTTSZoomPreference(newValue);
    },
    [localSettings, _setTTSZoomPreference],
  );

  const setVoice = useCallback(
    (newValue) => {
      localSettings.textToSpeech.voice = newValue;
      _setVoice(newValue);

      // Change voice while speaking is in progress by restarting the utterance
      if (isSpeakingRef.current) {
        read();
      }
    },
    [localSettings, _setVoice, read],
  );

  // Handles calling getVoices twice to ensure it has a value,
  // then gets the list of voices from RsSpeechAPI and filters out the unwanted voices.
  // Sets the Provider's voice state with an object <TTSControls> can use.
  const determineAvailableVoices = useCallback(async () => {
    if (!rsSpeechAPI) {
      return;
    }

    let voiceList;
    if (rsSpeechAPI.voicesList?.length) {
      voiceList = rsSpeechAPI.voicesList?.length;
    } else {
      voiceList = await rsSpeechAPI.getVoices();
      rsSpeechAPI.voicesList = voiceList;
    }

    if (!voiceList?.length) {
      return;
    }

    // Apply filtering to the voice list
    voiceList = filterVoiceList(voiceList);

    // Is a voice already set?
    // If not set Initial Voice
    if (
      !voiceList
        .map((voiceObject) => voiceObject.voiceURI)
        .includes(localSettings.textToSpeech.voice)
    ) {
      setVoice(getInitialVoice(voiceList, userAgent));
    }

    // Set the final version of available voices.
    setVoices(getVoiceMap(voiceList));

    // Once any voice is set, the TTS service is available.
    dispatch(TTSActions.setTTSAvailable(true));
  }, [localSettings, rsSpeechAPI, setVoice, userAgent]);

  // Sets onVoicesChanged for rsSpeechAPI once rsSpeechAPI exists.
  // determineAvailableVoices needs rsSpeechAPI.getVoices() to have a value.
  // getVoices will be empty the first time it is called (a known bug with speechSynthesis).
  useEffect(
    function updateOnVoicesChangedInrsSpeechAPI() {
      const overRideAndCallOnVoicesChanged = async () => {
        rsSpeechAPI.onVoicesChanged = determineAvailableVoices;
        await rsSpeechAPI.onVoicesChanged();
      };

      if (rsSpeechAPI) {
        overRideAndCallOnVoicesChanged();
      }
    },
    [rsSpeechAPI], // `determineAvailableVoices` intentionally left out of this array
  );

  useEffect(
    function speechSynthesisCleanup() {
      return () => {
        // Cleanup speech synthesis when component unmounts
        if (isSpeakingRef.current) {
          rsSpeechAPI.cancel();
        }
        removeTTSClass(frameDocument);
        removeSvg(frameDocument);
      };
    },
    [rsSpeechAPI, frameDocument],
  );

  useEffect(
    function listenForMobileAppEvents() {
      const handleMessage = (event) => {
        // Pause reading if the mobile app went back to shelf.
        if (event.data.type === 'MOBILE_APP_BACK_TO_SHELF') {
          pauseReading();
          return;
        }
      };

      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    },
    [pauseReading],
  );

  /**
   * Expose values and modifiers from provider
   */
  const values = useMemo(
    () => ({
      hasNext: state.hasNext,
      hasPrevious: state.hasPrevious,
      isPaused: state.isPaused,
      isSpeaking: state.isSpeaking,
      isTTSReady: state.isTTSAvailable && isDocumentReady,
      isTTSDialogOpen: state.isTTSDialogOpen,
      didClickTTSDialogFit: state.didClickTTSDialogFit,
      readFrom: state.readFrom,
      rate,
      voice,
      voices,
      volume,
      ttsZoomPreference,
    }),
    [
      state.hasNext,
      state.hasPrevious,
      isDocumentReady,
      state.isPaused,
      state.isSpeaking,
      state.isTTSAvailable,
      state.isTTSDialogOpen,
      state.didClickTTSDialogFit,
      state.readFrom,
      rate,
      voice,
      voices,
      volume,
      ttsZoomPreference,
    ],
  );

  const modifiers = useMemo(
    () => ({
      goToNext,
      goToPrevious,
      read,
      readFromBeginning,
      readFromCfi,
      pauseReading,
      stopReading,
      setVolume,
      setVoice,
      setRate,
      setTTSZoomPreference,
      setIsTTSDialogOpen: (isOpen) =>
        dispatch(TTSActions.setTTSDialogOpen(isOpen)),
      setDidClickTTSDialogFit: (didClick) =>
        dispatch(TTSActions.setDidClickTTSDialogFit(didClick)),
      setReadFrom: (readFrom) => dispatch(TTSActions.setReadFrom(readFrom)),
    }),
    [
      goToNext,
      goToPrevious,
      read,
      readFromBeginning,
      readFromCfi,
      pauseReading,
      stopReading,
      setVolume,
      setVoice,
      setRate,
      setTTSZoomPreference,
      dispatch,
    ],
  );

  /**
   * For debug
   */
  window.v7.tts = {
    ...values,
    ...modifiers,
  };

  return (
    <TextToSpeechValuesContext.Provider value={values}>
      <TextToSpeechModifiersContext.Provider value={modifiers}>
        {children}
        <AriaLiveRegion id="tts-announcement" isPolite={false} />
      </TextToSpeechModifiersContext.Provider>
    </TextToSpeechValuesContext.Provider>
  );
};

export default TextToSpeechProvider;
